<template>
    <b-card>
        <BrowsePropertyUnitModal 
            ref="browsePropertyUnitModal" 
            :show_browse_unit.sync="modals.show_browse_unit"
        />
        <PreviewModal ref="preview_modal"></PreviewModal>

        <!-- SUB_SALE Fees Modal -->
        <SubSaleFeesModal 
            ref="subSaleFeesModal"
            :show_fees_modal.sync="modals.show_fees"
        />
        

        <div class="d-flex align-items-center justify-content-between mb-4">
            <h3>Unit Types</h3>
            <div>
                <svg xmlns="http://www.w3.org/2000/svg" class="mr-1" width="16" height="16" viewBox="0 0 576 512">
                    <path fill="#73839C" d="M575.8 255.5c0 18-15 32.1-32 32.1h-32l.7 160.2c0 2.7-.2 5.4-.5 8.1V472c0 22.1-17.9 40-40 40h-16c-1.1 0-2.2 0-3.3-.1-1.4.1-2.8.1-4.2.1H392c-22.1 0-40-17.9-40-40v-88c0-17.7-14.3-32-32-32h-64c-17.7 0-32 14.3-32 32v88c0 22.1-17.9 40-40 40h-55.9c-1.5 0-3-.1-4.5-.2-1.2.1-2.4.2-3.6.2h-16c-22.1 0-40-17.9-40-40V360c0-.9 0-1.9.1-2.8v-69.6H32c-18 0-32-14-32-32.1 0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7l255.4 224.5c8 7 12 15 11 24"/>
                </svg>
                <span 
                    class="text-primary text-md font-weight-500 cursor-pointer"
                    @click="viewAllUnits()"
                >
                    Total {{ totalUnits }} units
                </span>
            </div>
        </div>

        <!-- SUB_SALE: Unit grid -->
        <div v-if="isSubSale">
            <div v-if="!filteredUnitByLevels.length" class="text-center py-4 text-muted">
                No Units Found.
            </div>
            <div v-else-if="unitCategory(project.project_type) !== 'landed'">
                <div class="table-select-unit mt-3">
                    <b-table-simple responsive bordered>
                        <b-tbody>
                            <b-tr v-for="(level, index) in filteredUnitByLevels" :key="index">
                                <!-- Clickable Level Label → Open Fees Modal -->
                                <b-td 
                                    class="font-weight-bold text-primary cursor-pointer py-3 align-middle"
                                    style="width: 140px;"
                                    @click="openFeesModal(level[0].floor_sort, level)"
                                >
                                    Level {{ level[0].floor_sort }}
                                </b-td>

                                <!-- Unit Cells -->
                                <b-td 
                                    v-for="(unit, uIndex) in level" 
                                    :key="uIndex"
                                    :style="{ background: UNIT_COLOR[unit.available_status], color: 'white' }"
                                    class="text-center py-3"
                                >
                                    <p class="font-weight-bold text-capitalize mb-1"
                                       :class="unit.available_status !== 'Not available' ? 'text-white' : 'text-muted'">
                                        {{ unit.available_status }}
                                    </p>
                                    <p class="font-weight-bold mb-1">
                                        RM {{ unit.unit_price | moneyFormatter }}
                                    </p>
                                    <span class="text-xs text-dark">
                                        {{ unit.unit_no }} | {{ unit.built_up }} sqft
                                    </span>
                                </b-td>
                                <b-td v-for="n in (maxColumns - level.length)" :key="'empty-' + n"></b-td>
                            </b-tr>
                        </b-tbody>
                    </b-table-simple>
                </div>
            </div>

            <!-- Landed layout (8 per row) -->
            <div v-else>
                <div class="alert alert-info">
                    Landed property fees display not implemented yet.
                </div>
            </div>
        </div>

        <!-- Primary Sale: Original summary table -->
        <div v-else-if="displayedUnitTypes.length > 0" class="table-simple-modal">
            <b-table-simple responsive bordered>
                <b-thead>
                    <b-tr>
                        <b-th>Unit Name</b-th>
                        <b-th class="text-right">Price From<br>/sqft</b-th>
                        <b-th class="text-right">Available<br>/Total</b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <b-tr v-for="(type, index) in displayedUnitTypes" :key="index">
                        <b-td>
                            <p class="text-primary font-weight-600 cursor-pointer" @click="previewFloorPlan(type)">
                                {{ type.unit_type_name }}
                            </p>
                            <div class="d-flex align-items-center">
                                <span class="mr-1">{{ type.bedroom }}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 56 56"><path fill="#73839C" d="M5.29 21.994h5.613v-3.179c0-2.064 1.16-3.178 3.248-3.178h8.235c2.088 0 3.248 1.114 3.248 3.178v3.178h5.01v-3.178c0-2.064 1.16-3.178 3.364-3.178h7.725c2.204 0 3.364 1.114 3.364 3.178v3.178h5.614V15.66c0-4.106-2.18-6.193-6.17-6.193H11.46c-3.967 0-6.17 2.087-6.17 6.193ZM0 44.867c0 1.137.742 1.856 1.902 1.856h1.3c1.136 0 1.878-.72 1.878-1.856v-3.294c.255.07.998.116 1.531.116h42.8c.534 0 1.254-.047 1.509-.116v3.294c0 1.137.742 1.856 1.879 1.856h1.322c1.137 0 1.879-.72 1.879-1.856v-13.2c0-4.199-2.343-6.495-6.588-6.495H6.588C2.343 25.172 0 27.468 0 31.667Z"/></svg>
                                <span class="ml-2 mr-1">{{ type.bathroom }}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 512 512"><path fill="#73839C" d="M96 77.3C96 70 101.9 64 109.3 64c3.5 0 6.9 1.4 9.4 3.9l14.9 14.9c-3.6 9-5.6 18.9-5.6 29.2 0 19.9 7.2 38 19.2 52-5.3 9.2-4 21.1 3.8 29 9.4 9.4 24.6 9.4 33.9 0L289 89c9.4-9.4 9.4-24.6 0-33.9-7.9-7.9-19.8-9.1-29-3.8C246 39.2 227.9 32 208 32c-10.3 0-20.2 2-29.2 5.5l-14.9-14.9C149.4 8.1 129.7 0 109.3 0 66.6 0 32 34.6 32 77.3V256c-17.7 0-32 14.3-32 32s14.3 32 32 32h448c17.7 0 32-14.3 32-32s-14.3-32-32-32H96zM32 352v16c0 28.4 12.4 54 32 71.6V480c0 17.7 14.3 32 32 32s32-14.3 32-32v-16h256v16c0 17.7 14.3 32 32 32s32-14.3 32-32v-40.4c19.6-17.6 32-43.1 32-71.6v-16z"/></svg>
                                <span class="ml-2 mr-1">{{ type.car_park }}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 512 512"><path fill="#73839C" d="M135.2 117.4 109.1 192h293.8l-26.1-74.6c-4.5-12.8-16.6-21.4-30.2-21.4H165.4c-13.6 0-25.7 8.6-30.2 21.4m-95.6 79.4L74.8 96.3C88.3 57.8 124.6 32 165.4 32h181.2c40.8 0 77.1 25.8 90.6 64.3l35.2 100.5c23.2 9.6 39.6 32.5 39.6 59.2v192c0 17.7-14.3 32-32 32h-32c-17.7 0-32-14.3-32-32v-48H96v48c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32V256c0-26.7 16.4-49.6 39.6-59.2M128 288a32 32 0 1 0-64 0 32 32 0 1 0 64 0m288 32a32 32 0 1 0 0-64 32 32 0 1 0 0 64"/></svg>
                            </div>
                        </b-td>
                        <b-td class="text-right">
                            <p class="font-weight-600">RM {{ type.price_from | moneyFormatter }}</p>
                            <p class="text-muted text-xs">{{ type.built_up }} sqft</p>
                        </b-td>
                        <b-td class="text-right">
                            <p class="text-primary font-weight-600 cursor-pointer" @click="viewUnit(type.unit_listing_group_by_level, project.project_type)">
                                {{ type.total_open_units }}
                            </p>
                            <p class="text-primary text-xs cursor-pointer" @click="viewUnit(type.unit_listing_group_by_level, project.project_type)">
                                / {{ type.total_units }} units
                            </p>
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </div>

        <div v-if="!isSubSale && displayedUnitTypes.length === 0" class="text-center py-4 text-muted">
            No unit types available
        </div>
    </b-card>
</template>

<script>
import BrowsePropertyUnitModal from '@/views/PropertyListing/BrowsePropertyUnitModal.vue'
import PreviewModal from '@/components/Modal/PreviewModal'
import SubSaleFeesModal from '@/views/PropertyListing/SubSaleFeesModal.vue'
import { notifyFunctionMixin, helperFunctionMixin } from '@/assets/js/httpImports.js'
import { UNIT_COLOR } from '@/assets/js/colors.js'

export default {
    props: ['project', 'unit_types'],
    mixins: [notifyFunctionMixin, helperFunctionMixin],
    components: {
        BrowsePropertyUnitModal,
        PreviewModal,
        SubSaleFeesModal,
    },
    data() {
        return {
            modals: {
                show_browse_unit: false,
                show_fees: false
            },
            selectedLevel: null,
            selectedLevelUnits: [],
            UNIT_COLOR
        }
    },
    computed: {
        isSubSale() {
            return this.project?.unit_highest?.sale_type === 'SUB_SALE'
        },
        totalUnits() {
            if (!this.project) return 0
            if (!this.isSubSale && this.project.counts && !this._gHlp.isEmpty(this.project.counts)) {
                return Object.values(this.project.counts).reduce((a, v) => a + parseInt(v || 0, 10), 0)
            }
            return (this.project?.unit_listing_group_by_level || []).reduce((s, f) => s + (f?.length || 0), 0)
        },
        filteredUnitByLevels() {
            if (!this.project?.unit_listing_group_by_level) return []
            return this.project.unit_listing_group_by_level
                .filter(level => level.length > 0 && level[0]?.floor_sort !== null)
                .sort((a, b) => parseInt(b[0].floor_sort) - parseInt(a[0].floor_sort))
        },
        maxColumns() {
            return Math.max(...this.filteredUnitByLevels.map(l => l.length), 1)
        },
        unitCategory() {
            const map = {
                'Condominium': 'non-landed',
                'Apartment': 'non-landed',
                'Service Apartment/ Residence': 'non-landed',
                '1-sty terrace/link house': 'landed',
                'Bungalow': 'landed',
                'Semi- D': 'landed'
            }
            return type => map[type] || 'non-landed'
        },
        displayedUnitTypes() {
            if (this.isSubSale) return []
            return Array.isArray(this.unit_types) ? this.unit_types : []
        }
    },
    methods: {
        viewAllUnits() {
            this.viewUnit(this.project.unit_listing_group_by_level, this.project.project_type)
        },
        viewUnit(units, project_type) {
            this.$refs.browsePropertyUnitModal.setUnits(units, project_type)
            this.modals.show_browse_unit = true
        },
        openFeesModal(floorSort, levelUnits) {
            this.$refs.subSaleFeesModal.setLevel(floorSort, levelUnits)
            this.modals.show_fees = true
        },
        previewFloorPlan(type) {
            if (this.$route.query.source_type === 'HESTIA') {
                this._gNotif.notifyInfo('Floor plan not available for this source')
                return
            }
            const url = type.floor_plan_image_url
            if (url) {
                this.$refs.preview_modal.preview(url)
            } else {
                this._gNotif.notifyError('Floor Plan not available')
            }
        }
    },
    filters: {
        moneyFormatter(val) {
            if (!val) return '0.00'
            const n = parseFloat(val)
            return isNaN(n) ? '0.00' : n.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        }
    }
}
</script>

<style scoped>
.cursor-pointer { cursor: pointer; }
.table-select-unit td {
    min-width: 130px;
    vertical-align: middle;
}
</style>